import styled from "styled-components";

import type { PetApi } from "../api/types";
import { useNow } from "../lib/useNow";
import { xpProgress } from "../lib/xp";

// XP progress toward the next level. Unlike the 0–100 stat Meters, the fill is a
// fraction interpolated from the time to the next passive XP tick, so it creeps
// forward live between backend polls. The readout is the fractional current XP.
const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  letter-spacing: 0.03em;
  text-transform: lowercase;
  color: ${({ theme }) => theme.color.textDim};
`;

const Glyph = styled.span`
  color: ${({ theme }) => theme.color.accent};
  font-size: 12px;
`;

const Badge = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.color.accent};
  background: ${({ theme }) => theme.color.accentSoft};
  border: 1px solid rgba(0, 152, 234, 0.35);
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: 1px 7px;
`;

const Value = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.text};
`;

const Track = styled.div`
  height: 6px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  border-radius: inherit;
  background: ${({ theme }) => theme.color.accent};
  box-shadow: 0 0 12px ${({ theme }) => theme.color.accentGlow};
  transition: width 0.3s ease;
`;

export function XpMeter({ pet }: { pet: PetApi }) {
  const now = useNow(1000);
  const { fillFraction, currentXp } = xpProgress(pet, now);
  const pct = Math.max(0, Math.min(100, fillFraction * 100));
  return (
    <Row>
      <Head>
        <Label>
          <Glyph>⬢</Glyph>
          xp
          <Badge>lv {pet.level}</Badge>
        </Label>
        <Value>
          {currentXp.toFixed(1)} / {Math.round(pet.xpForNext)}
        </Value>
      </Head>
      <Track>
        <Fill $pct={pct} />
      </Track>
    </Row>
  );
}
